import React from 'react';

/** Calendar day buckets from Postgres — format as dd/mm/yyyy without TZ shifting the day. */
function formatBucketDay(bucket) {
  if (bucket == null || bucket === '') return '';
  const ymd = String(bucket).split('T')[0];
  const parts = ymd.split('-');
  if (parts.length === 3) {
    const [y, m, d] = parts;
    if (y && m && d) return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  try {
    const dt = new Date(bucket);
    if (!Number.isNaN(dt.getTime())) {
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  } catch { /* fallthrough */ }
  return String(bucket);
}

/**
 * 7- or 30-day sparkline. Plain SVG, no dependencies. Renders a thin line +
 * area gradient so a row at-a-glance shows whether an issue is climbing,
 * flat, or fading — important for the regressions toggle.
 *
 * Pass `counts` as the daily-counts response from /api/issues/:id/timeline:
 *   [{ bucket: '2026-04-01', count: 3 }, ...]
 *
 * @param {boolean} [compact] — omit axis date labels (for tight KPI cells).
 */
export default function Sparkline({ counts = [], width = 80, height = 22, stroke = '#9a3412', compact = false }) {
  const rows = Array.isArray(counts) ? counts : [];
  const data = rows.map((c) => Number(c.count) || 0);
  if (!data.length) {
    return (
      <svg width={width} height={height} aria-hidden="true">
        <line x1="0" y1={height - 1} x2={width} y2={height - 1} stroke="var(--border-default)" />
      </svg>
    );
  }
  const max = Math.max(1, ...data);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 2) - 1;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const fill = `${path} L ${width} ${height} L 0 ${height} Z`;
  const firstBucket = rows[0]?.bucket;
  const lastBucket = rows[rows.length - 1]?.bucket;
  const startLabel = formatBucketDay(firstBucket);
  const endLabel = formatBucketDay(lastBucket);
  const showRange = startLabel && endLabel;

  return (
    <div className="w-full min-w-0">
      <svg width={width} height={height} aria-hidden="true" className="max-w-full">
        <path d={fill} fill={stroke} fillOpacity="0.12" />
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.25" />
      </svg>
      {showRange && !compact ? (
        <div
          className={`mt-1 flex text-[10px] tabular-nums text-[color:var(--text-muted)] ${
            startLabel === endLabel ? 'justify-center' : 'justify-between'
          }`}
        >
          <span>{startLabel}</span>
          {startLabel !== endLabel ? <span>{endLabel}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
